import { useEffect, useRef } from "react";

const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

// Calls `callback` every `intervalMs` while `enabled`. Waits for each call to finish before scheduling the next,
// pauses while the page is hidden (and calls straight away when it's shown again), and stops on unmount.
const usePolling = (callback, intervalMs, enabled) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;
    let timer = null;
    let running = false;
    let stopped = false;

    const schedule = () => {
      if (stopped || isHidden()) return;
      timer = setTimeout(tick, intervalMs);
    };

    async function tick() {
      timer = null;
      running = true;
      try {
        await callbackRef.current();
      } catch (err) {
        console.error("Polling failed:", err);
      } finally {
        running = false;
      }
      schedule();
    }

    const handleVisibility = () => {
      if (stopped) return;
      if (isHidden()) {
        clearTimeout(timer);
        timer = null;
      } else if (!timer && !running) {
        tick();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, intervalMs]);
};

export default usePolling;
